import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { IconProp } from 'models/Icon';
import { useTranslation } from 'react-i18next';
import useCallbackRef from 'hooks/useCallbackRef';
import { Button, Modal } from 'ncoded-component-library';

import './HighlightsSection.styles.scss';

export type HighlightsSectionProps = {
  titleLabel: string;
  listArray: string[];
  className?: string;
  icon: IconProp;
};

export const HighlightsSection: React.FC<HighlightsSectionProps> = (props) => {
  const { titleLabel, className, listArray = [], icon: Icon } = props;

  const [isOverflowing, setIsOverflowing] = useState(false);

  const resizeObserverRef = useRef<ResizeObserver>();

  const [contentContainer, contentContainerRef] = useCallbackRef();
  const [showMoreModal, showMoreModalRef] = useCallbackRef();

  const classes = classNames('highlights-section', className);

  const { t } = useTranslation();

  const Content = useMemo(
    () => (
      <div className="highlights-section__content-container">
        <label>
          <Icon />
          {t(titleLabel)}
        </label>

        {listArray?.length ? (
          <ul>
            {listArray.map((listItem, index) => (
              <li key={`highlights-section__listItem-${index}`}>{listItem}</li>
            ))}
          </ul>
        ) : (
          <p className="highlights-section__empty">{`${t('listingHasNo')} ${t(
            titleLabel,
          )}`}</p>
        )}
      </div>
    ),
    [Icon, listArray, t, titleLabel],
  );

  // calculating if content is overflowing to (or not to) show "show more" button
  useEffect(() => {
    if (!contentContainer) return;
    const onResize: ResizeObserverCallback = ([_]) => {
      const hasOverflowingChildren =
        contentContainer.offsetHeight < contentContainer.scrollHeight ||
        contentContainer.offsetWidth < contentContainer.scrollWidth;

      setIsOverflowing(hasOverflowingChildren);
    };

    if (!resizeObserverRef.current) {
      resizeObserverRef.current = new ResizeObserver(onResize);
      resizeObserverRef.current.observe(contentContainer as Element);
    }

    return () => {
      resizeObserverRef.current?.unobserve(contentContainer as Element);
    };
  }, [contentContainer]);

  return (
    <section className={classes} ref={contentContainerRef}>
      {Content}

      <div
        className={classNames('highlights-section__show-more-container', {
          visible: isOverflowing,
        })}
      >
        <Button variant="link" onClick={showMoreModal?.open}>
          {t('showMore')}
        </Button>
      </div>

      <Modal
        ref={showMoreModalRef}
        className="highlights-section-overflowing-content-preview"
      >
        <div>{Content}</div>
      </Modal>
    </section>
  );
};

export default HighlightsSection;
